import { Controller } from 'react-hook-form';

import SliderInput from '@/components/Form/Fields/SliderInput';
import type { DashboardParams } from '@/hooks/JobBoardFilters/useJobBoardParams';
import { useJobBoardParams } from '@/hooks/JobBoardFilters/useJobBoardParams';
import useForm from '@/hooks/useForm';

import ControlButtons from './ControlButtons';
import FilterSection from './FilterSection';

export const SALARY_FORM_ID = 'jb-filter-salary';

export const SALARY_MIN = 0;
export const SALARY_MAX = 500_000;
const SALARY_STEP = 10_000;

export type SalaryParams = Pick<DashboardParams, 'salaryLower' | 'salaryUpper'>;

const snap = (value: number) => Math.round(value / SALARY_STEP) * SALARY_STEP;

const toBound = (value: number, emptyAt: number) =>
  Number.isNaN(value) || snap(value) === emptyAt ? undefined : snap(value);

const Salary = ({ defaultValues }: { defaultValues: SalaryParams }) => {
  const { setParams } = useJobBoardParams();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      salary: [defaultValues.salaryLower, defaultValues.salaryUpper],
    },
  });

  return (
    <form
      id={SALARY_FORM_ID}
      className="w-full"
      onSubmit={handleSubmit(({ salary }) =>
        setParams({
          salaryLower: salary[0],
          salaryUpper: salary[1],
        })
      )}
      onReset={(event) => {
        event.preventDefault();
        reset({ salary: [undefined, undefined] });
      }}
    >
      <div className="max-w-5xl">
        <FilterSection title="Salary" subtitle="Choose salary">
          <div className="w-full">
            <Controller
              control={control}
              name="salary"
              render={({ field: { onChange, value } }) => (
                <SliderInput
                  value={value.map((bound) => bound ?? Number.NaN)}
                  onChange={([from, to]) =>
                    onChange([
                      toBound(from, SALARY_MIN),
                      toBound(to, SALARY_MAX),
                    ])
                  }
                  min={SALARY_MIN}
                  max={SALARY_MAX}
                  step={SALARY_STEP}
                  showInputField
                  placeholder={['No minimum', 'No maximum']}
                />
              )}
            />
          </div>
        </FilterSection>
      </div>
      <ControlButtons />
    </form>
  );
};

export default Salary;
